/**
 * `FloatingEdge` — a React Flow edge that anchors to node *borders* instead of fixed handles
 * (interactive-diagrams v0.4).
 *
 * Vanilla React Flow draws every edge between the source node's one handle and the target's one
 * handle, so arrows terminated at fixed top/bottom points that had nothing to do with where the
 * two tiles actually were — they appeared to end at random spots off the shape. This edge instead
 * computes each endpoint as the point where the center-to-center line crosses that tile's border
 * (`edgeAnchors`, pure geometry in `../edgeGeometry`), so an arrow always lands **on the shape,
 * pointing at the other node** — the Lucid/draw.io behavior. Live node measurements feed the math,
 * so it stays correct through drags, resizes, and relabels.
 */

import {
  BaseEdge,
  EdgeLabelRenderer,
  getSmoothStepPath,
  useInternalNode,
  Position,
  type EdgeProps,
  type InternalNode,
  type Node,
} from '@xyflow/react';
import { edgeAnchors, type Box, type BoxSide } from '../edgeGeometry';

/** RF `Position` for a geometry side — keeps the bezier control handles oriented correctly. */
const SIDE_TO_POSITION: Record<BoxSide, Position> = {
  top: Position.Top,
  right: Position.Right,
  bottom: Position.Bottom,
  left: Position.Left,
};

/**
 * An InternalNode → center+size box. Prefers the live-measured size, then the node's estimated
 * dimensions (`data.estW/estH`, seeded to match `initialWidth/Height`), then explicit size — so
 * the anchor lands on the real border even before (or without) a ResizeObserver measurement.
 */
function nodeBox(node: InternalNode<Node>): Box {
  const data = node.data as { estW?: number; estH?: number } | undefined;
  const w =
    node.measured?.width ?? data?.estW ?? (typeof node.width === 'number' ? node.width : undefined) ?? 100;
  const h =
    node.measured?.height ?? data?.estH ?? (typeof node.height === 'number' ? node.height : undefined) ?? 40;
  const p = node.internals.positionAbsolute;
  return { cx: p.x + w / 2, cy: p.y + h / 2, w, h };
}

export function FloatingEdge({
  id,
  source,
  target,
  markerEnd,
  markerStart,
  style,
  label,
}: EdgeProps): JSX.Element | null {
  const sourceNode = useInternalNode(source);
  const targetNode = useInternalNode(target);
  if (!sourceNode || !targetNode) return null;

  const { sx, sy, tx, ty, sourceSide, targetSide } = edgeAnchors(
    nodeBox(sourceNode),
    nodeBox(targetNode),
  );
  // Orthogonal (right-angle) routing with rounded corners — the Lucid/Visio look — anchored to
  // the borders `edgeAnchors` picked. Replaces the old diagonal bezier.
  const [edgePath, labelX, labelY] = getSmoothStepPath({
    sourceX: sx,
    sourceY: sy,
    sourcePosition: SIDE_TO_POSITION[sourceSide],
    targetPosition: SIDE_TO_POSITION[targetSide],
    targetX: tx,
    targetY: ty,
    borderRadius: 8,
  });

  return (
    <>
      <BaseEdge id={id} path={edgePath} markerEnd={markerEnd} markerStart={markerStart} style={style} />
      {label ? (
        <EdgeLabelRenderer>
          <div
            className="dg-edge-label nodrag nopan"
            style={{ transform: `translate(-50%, -50%) translate(${labelX}px, ${labelY}px)` }}
          >
            {label}
          </div>
        </EdgeLabelRenderer>
      ) : null}
    </>
  );
}
